import { lazy } from 'react';
import { Box, Text, Group, useMantineTheme, ThemeIcon } from '@mantine/core';
import { IconChecklist, IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import { WidgetProps } from '../../types';
import { registerWidget } from '../../registry';
import { useVariantTextSizes } from '../../hooks/useWidgetPreferences';
import { usePreferencesStore } from '../../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../../hooks/useAccessibilityStyles';

// ============================================================================
// TaskCountStat Widget Component - Headerless individual stat
// ============================================================================

function TaskCountStatWidget({ globalData, instanceId, settings }: WidgetProps) {
  const theme = useMantineTheme();
  // Get text sizes scaled for the current size variant
  const { headingSize, smallTextSize, iconSize } = useVariantTextSizes(settings?.sizeVariant);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const stats = globalData?.taskStats;
  const totalTasks = stats?.total || 0;
  const completedTasks = stats?.done || 0;

  // Calculate completion percentage
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Mock trend data
  const trend = 5.2;
  const trendUp = trend > 0;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <Group gap="xs" mb={4}>
        <ThemeIcon size={iconSize} variant="light" color="teal" radius="xl">
          <IconChecklist size={iconSize - 4} />
        </ThemeIcon>
      </Group>
      <Text size={headingSize} fw={700} lh={1.2} style={accessibilityStyles}>
        {totalTasks.toLocaleString()}
      </Text>
      <Text size={smallTextSize} c="dimmed" mb={4} style={accessibilityStyles}>
        Total Tasks
      </Text>
      <Group gap={4} justify="center">
        {trendUp ? (
          <IconTrendingUp size={Math.max(10, iconSize - 6)} color={theme.colors.green[6]} />
        ) : (
          <IconTrendingDown size={Math.max(10, iconSize - 6)} color={theme.colors.red[6]} />
        )}
        <Text size={smallTextSize} c={trendUp ? 'green' : 'red'} fw={500} style={accessibilityStyles}>
          {trendUp ? '+' : ''}{trend}%
        </Text>
      </Group>
    </Box>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'task-count-stat',
  name: 'Task Count',
  description: 'Single metric showing total task count with trend',
  icon: IconChecklist,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: TaskCountStatWidget })),
  sizes: {
    base: { cols: 6, rows: 2 },
    sm: { cols: 4, rows: 2 },
    md: { cols: 3, rows: 2 },
    lg: { cols: 2, rows: 2 },
    xl: { cols: 2, rows: 2 },
    tv: { cols: 2, rows: 2 },
  },
  sizeVariants: {
    variants: ['tiny', 'small', 'medium', 'large'],
    defaultVariant: 'small',
    variantSizes: {
      tiny: {
        base: { cols: 4, rows: 1 },
        sm: { cols: 3, rows: 1 },
        md: { cols: 2, rows: 1 },
        lg: { cols: 2, rows: 1 },
        xl: { cols: 2, rows: 1 },
        tv: { cols: 2, rows: 1 },
      },
      small: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 4, rows: 2 },
        md: { cols: 3, rows: 2 },
        lg: { cols: 2, rows: 2 },
        xl: { cols: 2, rows: 2 },
        tv: { cols: 2, rows: 2 },
      },
      medium: {
        base: { cols: 6, rows: 2 },
        sm: { cols: 6, rows: 2 },
        md: { cols: 4, rows: 2 },
        lg: { cols: 3, rows: 2 },
        xl: { cols: 3, rows: 2 },
        tv: { cols: 3, rows: 2 },
      },
      large: {
        base: { cols: 6, rows: 3 },
        sm: { cols: 6, rows: 3 },
        md: { cols: 4, rows: 3 },
        lg: { cols: 3, rows: 3 },
        xl: { cols: 3, rows: 3 },
        tv: { cols: 3, rows: 3 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['taskStats'],
  },
  hasHeader: false,
  displayStyle: 'stat',
  hasSettings: true,
  collapsible: false,
  expandable: false,
});

export default TaskCountStatWidget;
